import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Images, Plus, Trash2, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

interface PlankUploaderProps {
  plankGroupId: string;
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  /** Upload mode - affects labels and behavior */
  mode?: 'single' | 'multiple';
  /** Replaces the empty-state heading, for uploaders that take one kind of image */
  title?: string;
}

// Component for image with skeleton loading
const ImageWithSkeleton: React.FC<{
  src: string;
  alt: string;
  onLoad?: () => void;
}> = ({ src, alt, onLoad }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <>
      {!isLoaded && <Skeleton className="absolute inset-0 size-full" />}
      <img
        src={src}
        alt={alt}
        onLoad={() => {
          setIsLoaded(true);
          onLoad?.();
        }}
        className={cn(
          'size-full object-cover transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
      />
    </>
  );
};

// Sortable wrapper for a single plank thumbnail. Owns drag attributes + listeners.
const SortablePlank: React.FC<{
  id: string;
  index: number;
  file: File;
  blobUrl: string;
  isFirst: boolean;
  onRemove: () => void;
  onPreview: () => void;
}> = ({ id, index, file, blobUrl, isFirst, onRemove, onPreview }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 'auto',
    opacity: isDragging ? 0.85 : 1,
    cursor: isDragging ? 'grabbing' : 'pointer',
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // The PointerSensor's activation distance suppresses drag on simple
        // clicks, but dnd-kit still calls listeners; guard against the drag
        // state to avoid opening the preview at the end of a drag.
        if (isDragging) return;
        e.stopPropagation();
        onPreview();
      }}
      className={cn(
        'group relative size-20 overflow-hidden border-2 bg-background',
        isFirst ? 'border-foreground' : 'border-border'
      )}
    >
      <ImageWithSkeleton src={blobUrl} alt={`Plank ${index + 1}`} />
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={t('COMMON.REMOVE_IMAGE', 'Remove image') as string}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute top-1 end-1 bg-foreground/70 text-background opacity-0 transition-opacity hover:bg-destructive hover:text-white group-hover:opacity-100"
      >
        <X />
      </Button>
      {index > 0 && (
        <span className="absolute bottom-1 start-1 flex size-[18px] items-center justify-center rounded-full bg-foreground/60 text-[10px] font-medium text-background">
          {index + 1}
        </span>
      )}
    </div>
  );
};

/**
 * Drag-drop uploader for plank/tile images
 * Used within the variant table for multiple upload mode
 */
export const PlankUploader: React.FC<PlankUploaderProps> = ({
  plankGroupId,
  files,
  onChange,
  maxFiles,
  mode = 'multiple',
  title,
}) => {
  const { t } = useTranslation();
  const isSingleMode = mode === 'single' || maxFiles === 1;

  // Stable per-file IDs for dnd-kit (it needs ids that survive across reorders).
  // We keep a ref-backed Map<File, id> and reconcile on every render: new files
  // get a fresh id, removed files are dropped from the map.
  const fileIdsRef = useRef<Map<File, string>>(new Map());
  const nextIdRef = useRef(0);
  for (const f of files) {
    if (!fileIdsRef.current.has(f)) {
      fileIdsRef.current.set(f, `plank-${nextIdRef.current++}`);
    }
  }
  const presentFiles = new Set(files);
  for (const f of Array.from(fileIdsRef.current.keys())) {
    if (!presentFiles.has(f)) fileIdsRef.current.delete(f);
  }
  const fileItems = files.map((file) => ({ id: fileIdsRef.current.get(file)!, file }));

  // Object URLs are memoised per-file so an image isn't re-fetched on every
  // render and the browser can release them when a file is removed.
  const blobUrlsRef = useRef<Map<File, string>>(new Map());
  for (const f of files) {
    if (!blobUrlsRef.current.has(f)) {
      blobUrlsRef.current.set(f, URL.createObjectURL(f));
    }
  }
  for (const f of Array.from(blobUrlsRef.current.keys())) {
    if (!presentFiles.has(f)) {
      URL.revokeObjectURL(blobUrlsRef.current.get(f)!);
      blobUrlsRef.current.delete(f);
    }
  }
  useEffect(() => {
    // On unmount, release any remaining object URLs.
    return () => {
      for (const url of Array.from(blobUrlsRef.current.values())) {
        URL.revokeObjectURL(url);
      }
      blobUrlsRef.current.clear();
    };
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isSingleMode) {
        onChange(acceptedFiles.slice(0, 1));
      } else {
        const all = [...files, ...acceptedFiles];
        const newFiles = maxFiles ? all.slice(0, maxFiles) : all;
        onChange(newFiles);
      }
    },
    [files, onChange, maxFiles, isSingleMode]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
    },
    // 0 is react-dropzone's "no limit"
    maxFiles: isSingleMode ? 1 : maxFiles ? Math.max(1, maxFiles - files.length) : 0,
    multiple: !isSingleMode,
  });

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  // Drag activation distance prevents a click (or a tiny jitter on tap) from
  // starting a drag — without it, click-to-preview would always be intercepted.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fileItems.findIndex((it) => it.id === active.id);
    const newIndex = fileItems.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(files, oldIndex, newIndex));
  };

  // Preview dialog state
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Remove-all confirmation dialog
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const handleRemoveAll = () => {
    onChange([]);
    setConfirmClearOpen(false);
  };

  const sortableIds = useMemo(() => fileItems.map((it) => it.id), [fileItems]);

  return (
    <div>
      {/* Empty state - improved dropzone design */}
      {files.length === 0 ? (
        <div
          {...getRootProps()}
          className={cn(
            'cursor-pointer border-2 border-dashed p-8 text-center transition-colors',
            isDragActive ? 'border-foreground bg-accent' : 'border-border bg-background hover:border-foreground hover:bg-muted'
          )}
        >
          <input {...getInputProps()} />
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-muted">
            <Images className="size-8 text-muted-foreground" />
          </div>
          <p className="mb-1 text-sm font-medium text-foreground">
            {isDragActive
              ? isSingleMode
                ? t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.DROP_IMAGE_HERE')
                : t('PAGES.NEW_PRODUCT.V2.PLANK_UPLOADER.DROP_IMAGES_HERE')
              : title
                ?? (isSingleMode
                  ? t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.UPLOAD_SEAMLESS')
                  : t('PAGES.NEW_PRODUCT.V2.PLANK_UPLOADER.UPLOAD_PLANK_IMAGES'))}
          </p>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V2.PLANK_UPLOADER.DRAG_DROP_OR_BROWSE')}
          </p>
          <div className="inline-flex items-center gap-1 border bg-muted px-4 py-1.5">
            <Images className="size-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {isSingleMode ? t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.FILE_TYPES_SINGLE') : maxFiles ? t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.FILE_TYPES_MULTIPLE', { maxFiles }) : t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.FILE_TYPES_ANY')}
            </span>
          </div>
        </div>
      ) : (
        <>
          {/* Image grid with skeleton loading */}
          <div className="border bg-muted p-4">
            {/* Header: count + remove all (multi-mode only, >1 file) */}
            {!isSingleMode && files.length > 1 && (
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.IMAGES_COUNT', { count: files.length })}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmClearOpen(true)}
                  className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                >
                  <Trash2 data-icon="inline-start" />
                  {t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.REMOVE_ALL')}
                </Button>
              </div>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                <div className="flex flex-wrap gap-3">
                  {fileItems.map(({ id, file }, index) => (
                    <SortablePlank
                      key={id}
                      id={id}
                      index={index}
                      file={file}
                      blobUrl={blobUrlsRef.current.get(file)!}
                      isFirst={index === 0}
                      onRemove={() => handleRemoveFile(index)}
                      onPreview={() => setPreviewIndex(index)}
                    />
                  ))}

                  {/* Add more button */}
                  {(!maxFiles || files.length < maxFiles) && (
                    <div
                      {...getRootProps()}
                      className={cn(
                        'flex size-20 cursor-pointer flex-col items-center justify-center border-2 border-dashed transition-colors',
                        isDragActive
                          ? 'border-foreground bg-accent'
                          : 'border-border bg-background hover:border-foreground hover:bg-muted'
                      )}
                    >
                      <input {...getInputProps()} />
                      <Plus
                        className={cn(
                          'mb-0.5 size-6',
                          isDragActive ? 'text-foreground' : 'text-muted-foreground'
                        )}
                      />
                      <span className="text-[10px] font-medium text-muted-foreground">
                        {t('COMMON.ADD', { defaultValue: 'Add' })}
                      </span>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </>
      )}

      {/* Preview Dialog — click a plank to see it at full size */}
      <Dialog
        open={previewIndex !== null}
        onOpenChange={(next) => {
          if (!next) setPreviewIndex(null);
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          {previewIndex !== null && files[previewIndex] && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.PREVIEW_TITLE', { index: previewIndex + 1 })}
                </DialogTitle>
              </DialogHeader>
              <div className="flex max-h-[70vh] items-center justify-center overflow-hidden bg-muted">
                <img
                  src={blobUrlsRef.current.get(files[previewIndex])!}
                  alt={`Plank ${previewIndex + 1}`}
                  className="block max-h-[70vh] max-w-full object-contain"
                />
              </div>
              <p className="text-center text-xs break-all text-muted-foreground">
                {files[previewIndex].name}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove-all confirmation Dialog */}
      <Dialog
        open={confirmClearOpen}
        onOpenChange={(next) => {
          if (!next) setConfirmClearOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.REMOVE_ALL_CONFIRM_TITLE')}
            </DialogTitle>
            <DialogDescription>
              {t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.REMOVE_ALL_CONFIRM_MESSAGE', { count: files.length })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClearOpen(false)}>
              {t('COMMON.CANCEL')}
            </Button>
            <Button
              variant="outline"
              onClick={handleRemoveAll}
              className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
            >
              {t('PAGES.NEW_PRODUCT.V3.PLANK_UPLOADER.REMOVE_ALL')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
